import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText, {
  Opts as ButtonScaleViewWithTextOpts,
} from 'src/lib/ui/components/ButtonScaleViewWithText';
import View, { ViewOpts } from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import { FriendVisitDetails } from 'src/replicant/visit';
import LangBitmapFontTextView, {
  LangBitmapOpts,
} from 'src/lib/ui/components/LangBitmapFontTextView';
import {
  ImageSliceView,
  ImageSliceViewOpts,
  ImageView,
} from '@play-co/timestep-core/ui';
import ImageScaleView, {
  ImageScaleViewOpts,
} from '@play-co/timestep-core/lib/ui/ImageScaleView';
import popups from '../../../lib/ui/config/popups';
import { getTimestampText } from '../visit.util';
import { ImageViewOpts } from '@play-co/timestep-core/lib/ui/ImageView';
import { HudMeter, HudMeterOpts } from '../../components/HudMeter';
import * as Pet from 'src/replicant/pet';
import { Avatar, AvatarOpts } from '../../components/shared/Avatar';
import { getCreativeSuffix, withLoading } from 'src/lib/utils';
import { Feature } from 'src/replicant/analytics';
import { getPetSpeciesBucket } from 'src/replicant/pet/pet.getters';
import StateObserver from 'src/StateObserver';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { sendAddFriendRequest } from 'src/game/friends/friends.sequences';
import bitmapFonts from 'src/lib/bitmapFonts';
import {
  canAddFriends,
  isBuruFriend,
  isFriendshipRequestedSent,
} from 'src/replicant/friends/friends.getters';
import { getVisitDetails } from '../visit.sequence';

import { AssetGroup } from '@play-co/timestep-core';

namespace style {
  export const popup = {
    zIndex: 10000,
    width: uiConfig.popups.buruburu.width,
    height: 757,
    darkerBg: false,
  };

  export const message: LangBitmapOpts = {
    size: 30,
    height: 64,
    x: 52,
    y: 89,
    zIndex: 1,
  };
  message.width = popup.width - 2 * message.x!;

  export const underline: ViewOpts = {
    width: message.width,
    y: message.height,
    height: 4,
    backgroundColor: '#FDC381',
  };

  export const cancelButton: Omit<ButtonScaleViewWithTextOpts, 'tracking'> = {
    ...uiConfig.buttons.cancel,
    labelOffsetY: -1,
    fontSize: 46,
    font: 'Body',
    width: 230,
    height: 100,
    centerOnOrigin: true,
  };
  cancelButton.x = (popup.width - cancelButton.width!) / 2 - 10;
  cancelButton.y = popup.height - 25 - cancelButton.height! / 2;

  export const okButton: Omit<ButtonScaleViewWithTextOpts, 'tracking'> = {
    ...cancelButton,
    ...uiConfig.buttons.secondary,
  };
  okButton.x = (popup.width + okButton.width!) / 2 + 10;

  export namespace player {
    export const view: ImageSliceViewOpts = {
      x: 25,
      y: message.y! + message.height! + 25,
      height: 180,
      image: 'assets/ui/shared/frames/scroller_panel_blue.png',
      slices: {
        top: 19,
        left: 19,
        right: 22,
        bot: 28,
      },
    };
    view.width = popups.buruburu.width - 2 * view.x!;

    export const image: AvatarOpts = {
      centerOnOrigin: false,
      width: 128,
      height: 128,
      x: 16,
      y: 25,
    };

    export const name: LangBitmapOpts = {
      size: 30,
      font: 'Body',
      color: '#9A7171',
      verticalAlign: 'center',
      height: 48,
      x: image.x! + image.width! + 10,
      y: image.y! + 3,
    };
    name.width = view.width - name.x! - 20;

    export const visits: LangBitmapOpts = {
      font: name.font,
      color: name.color,
      x: name.x,
      y: name.y! + name.height!,
      height: 36,
      size: 22,
      align: 'left',
      verticalAlign: 'center',
    };
    visits.width = (view.width - visits.x!) / 2 - 15;

    export const visited: LangBitmapOpts = {
      ...visits,
    };
    visited.y! += visits.height!;

    export const lastLogin: LangBitmapOpts = {
      ...visits,
      align: 'right',
      wordWrap: true,
    };
    const margin = 10;
    lastLogin.width! -= margin;
    lastLogin.x! += visits.width + margin;
    lastLogin.height! *= 2;

    export const addFriendButton: Omit<
      ButtonScaleViewWithTextOpts,
      'tracking'
    > = {
      image: 'assets/ui/friends/button_secondary_small.png',
      imagePressed: 'assets/ui/friends/button_secondary_small.png', // TODO: Use proper image
      scaleMethod: '9slice' as const,
      sourceSlices: {
        horizontal: { left: 38, right: 38 },
        vertical: { top: 24, bottom: 24 },
      },
      pressedOffsetY: 5,
      width: 180,
      height: 55,
      x: view.width! - 197,
      y: 15,
      fontSize: 24,
      font: bitmapFonts('Body'),
    };

    export const requestedLabelFrame: ImageViewOpts = {
      image: 'assets/ui/friends/requested_label.png',
      x: addFriendButton.x,
      y: addFriendButton.y,
      width: addFriendButton.width!,
      height: addFriendButton.height!,
    };

    export const requestedLabel: LangBitmapOpts = {
      font: 'Body',
      color: '#7191AB',
      x: requestedLabelFrame.width! / 2,
      y: requestedLabelFrame.height! / 2,
      centerOnOrigin: true,
      width: 120,
      height: 29,
      size: 24,
      align: 'center',
      verticalAlign: 'center',
    };
  }

  export namespace pet {
    export const view: ViewOpts = {
      x: 25,
      y: player.view.y! + player.view.height! + 10,
      height: 180,
    };
    view.width = popups.buruburu.width - 2 * view.x!;

    export const speciesIconBackdrop: ImageViewOpts = {
      image: 'assets/ui/shared/frames/backdrop_panel.png',
      x: player.image.x,
      y: player.image.y,
      width: player.image.width,
      height: player.image.height,
    };

    export const speciesIcon: ImageScaleViewOpts = {
      x: speciesIconBackdrop.width! / 2,
      y: speciesIconBackdrop.height! / 2,
      width: speciesIconBackdrop.width! - 10,
      height: speciesIconBackdrop.height! - 10,
      scaleMethod: 'contain',
      centerOnOrigin: true,
    };

    export const name: LangBitmapOpts = {
      size: 30,
      font: 'Body',
      color: '#9A7171',
      verticalAlign: 'center',
      width: 130,
      height: 48,
      x: speciesIconBackdrop.x! + speciesIconBackdrop.width! + 10,
      y: speciesIconBackdrop.y! + 3,
    };

    export const species: LangBitmapOpts = {
      font: name.font,
      color: name.color,
      x: name.x,
      y: name.y! + name.height!,
      width: name.width,
      height: 36,
      size: 22,
      align: 'left',
      verticalAlign: 'center',
    };

    export const age: LangBitmapOpts = {
      ...species,
    };
    age.y! += age.height!;

    export const stomach: HudMeterOpts = {
      ...uiConfig.hud.stomachMeter,
      label: uiConfig.hud.meterLabel,
      text: uiConfig.hud.meterText,
    };
    stomach.view = {
      ...stomach.view,
      x: name.x! + name.width! + 10,
      y: 20,
    };

    export const love: HudMeterOpts = {
      ...uiConfig.hud.loveMeter,
      label: uiConfig.hud.meterLabel,
      text: uiConfig.hud.meterText,
    };
    love.view = {
      ...love.view,
      x: stomach.view.x! + stomach.view.width!,
      y: stomach.view.y,
    };
  }

  export const dashedLine: ImageViewOpts = {
    image: 'assets/ui/shared/frames/dashed_line.png',
    width: 389,
    height: 10,
    x: popup.width / 2,
    y: pet.view.y! + pet.view.height! + 20,
    centerOnOrigin: true,
  };

  export const disclaimer: LangBitmapOpts = {
    y: dashedLine.y! + dashedLine.height!,
    height: 48,
    align: 'center',
    verticalAlign: 'center',
    color: pet.name.color,
    font: 'Body',
    size: 28,
  };
  disclaimer.width = popup.width;
}

type CloseHandler = (result?: FriendVisitDetails) => void;

export default class PopupConfirmVisitFriend extends PopupBasic {
  private buttonCancel: ButtonScaleViewWithText;
  private buttonOk: ButtonScaleViewWithText;
  private close: CloseHandler;
  private playerPhoto!: Avatar;
  private playerName!: LangBitmapFontTextView;
  private visits!: LangBitmapFontTextView;
  private visited!: LangBitmapFontTextView;
  private lastLogin!: LangBitmapFontTextView;
  private speciesIcon!: ImageScaleView;
  private petName!: LangBitmapFontTextView;
  private species!: LangBitmapFontTextView;
  private age!: LangBitmapFontTextView;
  private stomach!: HudMeter;
  private love!: HudMeter;
  private details?: FriendVisitDetails;
  private addFriend!: ButtonScaleViewWithText;
  private requestedLabel!: ImageView;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      ...style.popup,
      superview: opts.superview,
      close: () => opts.close(),
      tracking: {
        feature: Feature.VISIT._,
        $subFeature: Feature.VISIT.POPUP_CONFIRM_VISIT._,
      },
      titleIcon: { image: 'assets/ui/visit/icons/visitfriend_icon.png' },
    });

    this.close = opts.close;
    this.buttonClose.removeFromSuperview();

    this.message.updateOpts(style.message);

    this.message.addSubview(new View(style.underline));

    this.box.addSubview(this.createPlayerView());

    this.box.addSubview(this.createPetView());

    this.box.addSubview(new ImageView(style.dashedLine));

    this.box.addSubview(
      new LangBitmapFontTextView({
        ...style.disclaimer,
        localeText: () => i18n('friendsVisit.confirm.disclaimer'),
      }),
    );

    this.buttonCancel = this.box.addSubview(
      new ButtonScaleViewWithText({
        ...style.cancelButton,
        localeText: () => i18n('basic.cancel'),
        onClick: async () => {
          sounds.sfx.play('cancel');
          return opts.close();
        },
        tracking: {
          feature: Feature.VISIT._,
          $subFeature: Feature.VISIT.POPUP_CONFIRM_VISIT.CLOSE,
        },
      }),
    );

    this.buttonOk = this.box.addSubview(
      new ButtonScaleViewWithText({
        ...style.okButton,
        localeText: () => i18n('friendsVisit.confirm.ok'),
        onClick: async () => {
          // Bail if details are not loaded yet. The button is blocked while fetching visit details, but
          // the fetch can fail, then the button would be clickable with undefined details.
          if (!this.details) return;

          sounds.sfx.play('ok');

          return opts.close(this.details);
        },
        tracking: {
          feature: Feature.VISIT._,
          $subFeature: Feature.VISIT.POPUP_CONFIRM_VISIT.OK,
        },
      }),
    );
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/shared/frames/scroller_panel_blue.png',
      'assets/ui/friends/button_secondary_small.png',
      'assets/ui/friends/requested_label.png',
      'assets/ui/shared/frames/backdrop_panel.png',
      'assets/ui/shared/frames/dashed_line.png',
      'assets/ui/visit/icons/visitfriend_icon.png',
    ]);
  }

  public init({ friendId }: { friendId: string }) {
    super.init({
      title: i18n('friendsVisit.confirm.title'),
      message: i18n('friendsVisit.confirm.message'),
    });

    void this.loadPlayerData(friendId);
  }

  private async loadPlayerData(friendId: string) {
    const data = await withLoading(() => getVisitDetails(friendId).get(), {
      feature: Feature.VISIT._,
      subFeature: Feature.VISIT.DETAILS,
    });

    if (!data) {
      // If failed to fetch data, show error message and close the popup.
      openPopupPromise('popupError', {
        message: i18n(`match2.team.errors.tryAgain`),
      }).then(() => this.close());
      return;
    }

    this.details = data;

    const { user } = StateObserver.getState();
    const evolutions = Pet.ruleset.evolutions[getPetSpeciesBucket(user)];
    const species = data.currentEvolution.species;
    const isDead = this.details.state === 'dead';

    this.playerPhoto.updateImage(data.photo!);
    this.playerPhoto.setFrame(data.frame);
    this.playerName.localeText = () => data.name!;
    this.visits.localeText = () => i18n('friendsVisit.confirm.visits', data);
    this.visited.localeText = () => i18n('friendsVisit.confirm.visited', data);
    this.lastLogin.localeText = () =>
      `${i18n('friendsVisit.popup.lastLogin')}\n${getTimestampText(
        data.lastLogin,
      )}`;

    this.speciesIcon.setImage(
      `assets/species/${species}/${isDead ? 'dead' : 'icon'}.png`,
    );
    this.petName.localeText = () => data.attributes.name;
    this.species.localeText = () =>
      evolutions[species]?.[`name_${getCreativeSuffix()}`];
    this.age.localeText = () => i18n('friendsVisit.confirm.age', data);
    const { max } = Pet.ruleset.attributeBounds;
    this.stomach.setMeterValue(data.attributes.stomach / max);
    this.love.setMeterValue(data.attributes.love / max);

    // Already friends, hide add friend UI
    if (isBuruFriend(user, data.id) || !canAddFriends(user)) {
      this.addFriend.style.visible = false;
      this.requestedLabel.style.visible = false;
    } else if (isFriendshipRequestedSent(user, data.id)) {
      // Already sent friend request, show requested label only
      this.addFriend.style.visible = false;
      this.requestedLabel.style.visible = true;
    } else {
      // Show add friend button only
      this.addFriend.style.visible = data.canAcceptFriendRequest; // Because it's an ab test not all users have friend request feature
      this.requestedLabel.style.visible = false;
    }
  }

  private createPlayerView() {
    const view = new ImageSliceView(style.player.view);

    this.playerPhoto = new Avatar({
      ...style.player.image,
      superview: view,
    });

    this.playerName = view.addSubview(
      new LangBitmapFontTextView(style.player.name),
    );

    this.visits = view.addSubview(
      new LangBitmapFontTextView(style.player.visits),
    );

    this.visited = view.addSubview(
      new LangBitmapFontTextView(style.player.visited),
    );

    this.lastLogin = view.addSubview(
      new LangBitmapFontTextView(style.player.lastLogin),
    );

    // Instead addFriend button, if user already added friend show requested label
    this.requestedLabel = view.addSubview(this.createRequestedLabel());

    // Add friend button
    this.addFriend = view.addSubview(this.createAddFriendButton());

    return view;
  }

  /**
   * Create requested label
   */
  private createRequestedLabel() {
    const frame = new ImageView({
      ...style.player.requestedLabelFrame,
      visible: false,
    });

    new LangBitmapFontTextView({
      superview: frame,
      ...style.player.requestedLabel,
      localeText: () => i18n('friends.requested'),
    });

    return frame;
  }

  /**
   * Create add friend button
   */
  private createAddFriendButton() {
    return new ButtonScaleViewWithText({
      ...style.player.addFriendButton,
      visible: false,
      preventDefault: true,
      localeText: () => i18n('friends.addFriend'),
      onClick: async () => {
        sounds.sfx.play('ok');
        await this.onAddFriend(this.details!);
      },
      tracking: {
        feature: Feature.VISIT._,
        $subFeature: Feature.VISIT.POPUP_VISIT_FRIEND.ADD_FRIEND,
      },
    });
  }

  /**
   * On add friend button click
   */
  private async onAddFriend(details: FriendVisitDetails) {
    const result = await sendAddFriendRequest(
      {
        id: details.id,
        name: details.name ?? '',
        profilePicture: details.photo ?? '',
        frame: details.frame,
        species: details.currentEvolution.species,
      },
      {
        sourceFeature: 'visit',
        originFeature: 'visit',
        originSubFeature: 'visit_profile',
      },
    );

    this.requestedLabel.style.visible = result;
    this.addFriend.style.visible = !result;
  }

  private createPetView() {
    const view = new ImageSliceView(style.pet.view);

    const speciesIconBackdrop = view.addSubview(
      new ImageView(style.pet.speciesIconBackdrop),
    );
    this.speciesIcon = speciesIconBackdrop.addSubview(
      new ImageScaleView({
        ...style.pet.speciesIcon,
      }),
    );

    this.petName = view.addSubview(new LangBitmapFontTextView(style.pet.name));

    this.species = view.addSubview(
      new LangBitmapFontTextView(style.pet.species),
    );

    this.age = view.addSubview(new LangBitmapFontTextView(style.pet.age));

    this.stomach = view.addSubview(new HudMeter(style.pet.stomach));
    this.stomach.setMeterValue(0);

    this.love = view.addSubview(new HudMeter(style.pet.love));
    this.love.setMeterValue(0);

    return view;
  }
}
